import React from 'react';
import { Link } from 'react-router-dom';

function WorkbookAbout() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream-50 to-white">
      {/* Header */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/workbook" className="inline-flex items-center text-cream-700 hover:text-cream-800 mb-8 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Workbook
          </Link>
          
          <h1 className="text-5xl md:text-6xl font-light text-charcoal mb-6">
            Why I Created This Workbook
          </h1>
          <p className="text-xl text-charcoal/70 leading-relaxed">
            The story behind the Boundary Reset Workbook and how it can help you reclaim your time, energy, and confidence.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12">
              <h2 className="text-3xl font-light text-charcoal mb-6">My Story</h2>
              <p className="text-charcoal/80 leading-relaxed mb-6">
                After years of working with clients who struggled with people-pleasing and boundaries, I noticed a common pattern: most people knew they *should* set boundaries, but they had no idea *how* to actually do it.
              </p>
              <p className="text-charcoal/80 leading-relaxed mb-6">
                They'd come to therapy saying things like:
              </p>
              <ul className="space-y-3 text-charcoal/80 mb-6">
                <li className="flex items-start gap-3">
                  <span className="text-cream-600 text-2xl">•</span>
                  <span>"I know I need boundaries, but I don't know where to start."</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cream-600 text-2xl">•</span>
                  <span>"Every time I try to say no, I feel so guilty that I give in."</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cream-600 text-2xl">•</span>
                  <span>"I need the exact words to say because my mind goes blank in the moment."</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cream-600 text-2xl">•</span>
                  <span>"I've tried before, but I always end up back where I started."</span>
                </li>
              </ul>
              <p className="text-charcoal/80 leading-relaxed mb-6">
                That's when I realized: people don't just need *insight*—they need *tools*. They need step-by-step guidance, practical scripts, and a way to track their progress so they can see they're actually changing.
              </p>
              <p className="text-charcoal/80 leading-relaxed">
                So I created the Boundary Reset Workbook—a practical, self-paced resource that gives you everything I teach my therapy clients about boundaries, all in one place.
              </p>
            </div>

            {/* Who It's For */}
            <div className="bg-cream-50 rounded-2xl p-8 md:p-12 mb-12">
              <h2 className="text-3xl font-light text-charcoal mb-6">Who This Workbook Is For</h2>
              <p className="text-charcoal/80 leading-relaxed mb-6">
                This workbook is designed for:
              </p>
              
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">🎯 People-Pleasers</h3>
                  <p className="text-charcoal/70">
                    If you often put others first, struggle to say no, and feel exhausted from overcommitting—this is for you. You'll learn to identify your patterns and break free from people-pleasing habits.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">💼 Professionals Who Overextend</h3>
                  <p className="text-charcoal/70">
                    If you take on extra projects, work late often, and struggle to leave work at work—this workbook will help you reclaim your time and set firm boundaries around your work hours.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">👨‍👩‍👧‍👦 Family Boundary Strugglers</h3>
                  <p className="text-charcoal/70">
                    If family dynamics leave you feeling drained, obligated, or resentful—this workbook includes specific scripts and strategies for setting boundaries with family members without burning bridges.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">💑 Relationship Over-Givers</h3>
                  <p className="text-charcoal/70">
                    If you lose yourself in relationships, prioritize your partner's needs over your own, and struggle to maintain your identity—this workbook will help you build boundaries while staying connected.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">🔄 Therapy Supplement Seekers</h3>
                  <p className="text-charcoal/70">
                    If you're already in therapy and want additional resources to practice between sessions—this workbook is a perfect complement to ongoing therapeutic work.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl">
                  <h3 className="text-xl font-medium text-charcoal mb-3">💰 Budget-Conscious Helpers</h3>
                  <p className="text-charcoal/70">
                    If therapy isn't in your budget right now but you need support with boundaries—this workbook gives you professional-quality guidance at an accessible price point.
                  </p>
                </div>
              </div>
            </div>

            {/* What Makes It Different */}
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12">
              <h2 className="text-3xl font-light text-charcoal mb-6">What Makes This Different?</h2>
              <p className="text-charcoal/80 leading-relaxed mb-8">
                There are plenty of boundary books and resources out there. Here's what makes the Boundary Reset Workbook unique:
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-cream-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-lg">1</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-charcoal mb-2">It's Action-Oriented</h3>
                    <p className="text-charcoal/70">
                      This isn't just theory—every page includes practical exercises you can do right now. You'll leave each section with something concrete to apply to your life.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-cream-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-lg">2</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-charcoal mb-2">It Addresses the Emotional Stuff</h3>
                    <p className="text-charcoal/70">
                      Most boundary resources skip over the guilt, fear, and anxiety that come with boundary-setting. This workbook dedicates an entire module to helping you work through those feelings.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-cream-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-lg">3</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-charcoal mb-2">It Gives You the Exact Words</h3>
                    <p className="text-charcoal/70">
                      One of the biggest barriers to setting boundaries is not knowing what to say. This workbook includes 50+ ready-to-use scripts for every common situation.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-cream-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-lg">4</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-charcoal mb-2">It's Self-Paced</h3>
                    <p className="text-charcoal/70">
                      Work through it at your own speed, on your own schedule. No pressure, no deadlines—just progress at a pace that feels right for you.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-cream-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-lg">5</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-charcoal mb-2">It Tracks Your Progress</h3>
                    <p className="text-charcoal/70">
                      You'll see evidence of your growth through weekly check-ins, wins tracking, and reflection prompts. This helps you stay motivated and recognize how far you've come.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* My Approach */}
            <div className="bg-cream-50 rounded-2xl p-8 md:p-12 mb-12">
              <h2 className="text-3xl font-light text-charcoal mb-6">My Approach to Boundaries</h2>
              <p className="text-charcoal/80 leading-relaxed mb-6">
                I believe boundaries aren't about building walls or cutting people off—they're about creating clarity and protecting your energy so you can show up authentically in your relationships.
              </p>
              <p className="text-charcoal/80 leading-relaxed mb-6">
                Healthy boundaries aren't rigid or harsh. They're flexible, compassionate, and rooted in self-awareness. They allow you to say no without guilt, yes without resentment, and create space for genuine connection.
              </p>
              <p className="text-charcoal/80 leading-relaxed mb-6">
                This workbook is grounded in evidence-based approaches including:
              </p>
              <ul className="space-y-3 text-charcoal/80">
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-cream-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>Cognitive Behavioral Therapy (CBT)</strong> – Challenge unhelpful thoughts about boundaries</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-cream-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>Acceptance and Commitment Therapy (ACT)</strong> – Accept discomfort while moving toward values</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-cream-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>Compassion-Focused Therapy</strong> – Build self-compassion through the process</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-cream-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>Assertiveness Training</strong> – Learn direct, respectful communication</span>
                </li>
              </ul>
            </div>

            {/* Success Stories */}
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-3xl font-light text-charcoal mb-6">Real Results from Real People</h2>
              
              <div className="space-y-8">
                <div className="border-l-4 border-cream-600 pl-6">
                  <p className="text-charcoal/80 italic mb-4">
                    "I've read boundary books before, but this was different. The exercises actually made me DO the work instead of just reading about it. Within 2 weeks, I said no to my boss's request to work over the weekend—and didn't feel guilty about it!"
                  </p>
                  <p className="text-charcoal/60 text-sm">— Rachel, 34</p>
                </div>
                
                <div className="border-l-4 border-cream-600 pl-6">
                  <p className="text-charcoal/80 italic mb-4">
                    "The scripts were life-changing. I used to freeze up when someone asked me for something. Now I have go-to phrases I can use in the moment. My anxiety around saying no has decreased SO much."
                  </p>
                  <p className="text-charcoal/60 text-sm">— Marcus, 28</p>
                </div>
                
                <div className="border-l-4 border-cream-600 pl-6">
                  <p className="text-charcoal/80 italic mb-4">
                    "I was skeptical about a workbook making a difference, but the progress tracking section helped me see how far I'd come. Celebrating my wins kept me motivated to keep going."
                  </p>
                  <p className="text-charcoal/60 text-sm">— Tanya, 41</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-cream-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-light text-charcoal mb-6">
            Ready to Start Your Boundary Journey?
          </h2>
          <p className="text-xl text-charcoal/70 mb-8">
            Get instant access to the complete workbook for just $29.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/workbook/get-started"
              className="inline-block bg-cream-600 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-cream-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Get the Workbook
            </Link>
            <Link 
              to="/workbook/preview"
              className="inline-block bg-white text-cream-700 border-2 border-cream-600 px-8 py-4 rounded-full text-lg font-medium hover:bg-cream-50 transition-all duration-300"
            >
              Preview Inside
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default WorkbookAbout;
